import toast from "@/components/ui/toast";
import type { ShareVoiceSidebarCategory } from "@/data-access/share-of-voice/queries/shareOfVoice-sidebar-filter-queries";
import type {
  DictionaryItem,
  SidebarFilterCategory,
} from "@/data-access/shared/queries/sidebar-filters-dictionary-queries";
import { GetSidebarFilters } from "@/data-access/shared/queries/sidebar-filters-dictionary-queries";
import { client } from "@/lib/urqlProvider";
import type { StateCreator } from "zustand";
import { create } from "zustand";
import { type DevtoolsOptions, devtools } from "zustand/middleware";

/**
 * Zustand slice for managing static filter dictionaries and their related enums.
 *
 * Fetches and stores static filter data (categoryLabel → human-readable label) from the GraphQL
 * sidebarFilters query. Replaces repetitive static constant mappings with a single source of truth
 * that can be shared across News Feed and Share of Voice modules.
 *
 * Stores dictionary data by category (TIER, PROMINENCE, SENTIMENT) for efficient lookup and reuse.
 */
export type FilterDictionarySlice = {
  sidebarFilters: SidebarFilterCategory[];
  isLoading: boolean;
  error: string | null;

  // Actions
  clearDictionary: () => void;
  getDictionaryForCategory: (
    category: ShareVoiceSidebarCategory,
  ) => DictionaryItem[] | undefined;
  getCategories: () => ShareVoiceSidebarCategory[];
  fetchSidebarFilters: () => Promise<void>;
};

const createFilterDictionarySlice: StateCreator<
  FilterDictionarySlice,
  [["zustand/devtools", never]]
> = (set, get) => ({
  sidebarFilters: [],
  isLoading: false,
  error: null,

  clearDictionary: () => {
    set({
      sidebarFilters: [],
      isLoading: false,
      error: null,
    });
  },

  getDictionaryForCategory: (category) => {
    const sidebarFilters = get().sidebarFilters;
    return sidebarFilters.find((filter) => filter.category === category)?.items;
  },

  getCategories: () => {
    const sidebarFilters = get().sidebarFilters;
    return sidebarFilters.map((filter) => filter.category);
  },

  fetchSidebarFilters: async () => {
    set({ isLoading: true, error: null });

    try {
      const result = await client.query(GetSidebarFilters, {}).toPromise();

      if (result.error) {
        toast.error("Failed to fetch sidebar filters.");
        set({ isLoading: false });
        return;
      }

      if (!result.data?.sidebarFilters) {
        toast.error("Failed to fetch sidebar filters.");
        set({ isLoading: false });
        return;
      }

      set({
        sidebarFilters: result.data.sidebarFilters,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      console.error("Error fetching sidebar filters:", error);
      toast.error("An error occurred while fetching sidebar filters.");
      set({ isLoading: false });
    }
  },
});

const useFilterDictionary = create<FilterDictionarySlice>()(
  devtools(createFilterDictionarySlice, {
    name: "static-filter-dictionary-store",
    serialize: {
      options: true,
    },
  } as unknown as DevtoolsOptions),
);

export default useFilterDictionary;
